import { Link } from "react-router-dom";
import { Heart, Star } from "lucide-react";
import { inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function ProductCard({ product, showAdd = true }) {
  const { addItem } = useCart();
  const image = (product.images || [])[0] || "/plant-placeholder.png";
  const discount = product.mrp && product.mrp > product.price
    ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
    : 0;
  const outOfStock = !(product.stock > 0);
  // Prefer a variant that actually has stock — the first-listed size isn't necessarily the one
  // that's available, and quick-add has no size picker of its own to let the shopper choose.
  const defaultVariant = product.variants?.find((v) => v.stock > 0) ?? product.variants?.[0];

  return (
    <div data-testid={`product-card-${product.id}`} className="card-hover group bg-white rounded-2xl border border-[var(--yp-border)] overflow-hidden flex flex-col">
      <Link to={`/product/${product.id}`} className={`relative aspect-[4/5] bg-[var(--yp-surface-alt)] overflow-hidden ${outOfStock ? "opacity-60" : ""}`}>
        <img src={image} alt={product.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
        {outOfStock ? (
          <span data-testid={`out-of-stock-${product.id}`} className="absolute top-3 left-3 bg-[var(--yp-error)] text-white text-[11px] font-semibold px-2 py-1 rounded-full">Out of Stock</span>
        ) : discount > 0 && (
          <span className="absolute top-3 left-3 bg-[var(--yp-primary)] text-white text-[11px] font-semibold px-2 py-1 rounded-full">-{discount}%</span>
        )}
        {product.air_purifying && (
          <span className="absolute top-3 right-3 chip bg-white/95 text-[10px]">Air-purifying</span>
        )}
      </Link>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <Link to={`/product/${product.id}`} className="font-serif-display text-lg leading-tight text-[var(--yp-text)] hover:text-[var(--yp-primary)]">
          {product.name}
        </Link>
        {product.scientific_name && (
          <div className="text-xs italic text-[var(--yp-muted)] -mt-1">{product.scientific_name}</div>
        )}
        <div className="flex items-center gap-1 text-xs text-[var(--yp-muted)]">
          <Star size={12} className="fill-[var(--yp-accent)] text-[var(--yp-accent)]" />
          <span className="font-medium text-[var(--yp-text)]">{product.rating?.toFixed(1) || "4.6"}</span>
          <span>· {product.review_count || 0} reviews</span>
        </div>
        <div className="mt-1 flex items-baseline gap-2">
          <div className="text-lg font-semibold text-[var(--yp-primary)]">{inr(product.price)}</div>
          {product.mrp > product.price && (
            <div className="text-xs line-through text-[var(--yp-muted)]">{inr(product.mrp)}</div>
          )}
        </div>
        {showAdd && (
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={(e) => { e.preventDefault(); addItem(product, defaultVariant?.variant_id); }}
            disabled={outOfStock}
            className="mt-2 w-full btn-primary rounded-full py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {outOfStock ? "Out of Stock" : "Add to Cart"}
          </button>
        )}
      </div>
    </div>
  );
}
